import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import { rpn, isCritical } from "@/components/blueprints/FmeaBlueprint";

const STEPS = [
  { n: 1, code: "1", label: "Severity", phase: "OPERATIONS", field: "severity", help: "1 = Negligible · 10 = Catastrophic asset/safety loss" },
  { n: 2, code: "2", label: "Occurrence", phase: "OPERATIONS", field: "occurrence", help: "1 = Highly Improbable · 10 = Inevitable" },
  { n: 3, code: "3", label: "Detection", phase: "OPERATIONS", field: "detection", help: "1 = Certain to Catch · 10 = Completely Blind" },
];
const LAST = 3;
const uid = () => "fm_" + Math.random().toString(36).slice(2, 10);
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const selCls = "h-10 rounded-sm border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function Fmea() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setItems(res.data.payload?.items || []); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled FMEA Grid", payload: { items }, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, items]);

  const cur = STEPS[step - 1];
  const addItem = () => setItems((x) => [...x, { id: uid(), step: "", mode: "", severity: 1, occurrence: 1, detection: 1 }]);
  const setItem = (i, patch) => setItems((x) => x.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setItems((x) => x.filter((_, j) => j !== i));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Risk grid finalised"); navigate(`/fmea/${id}/view`); } };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="fmea-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="FMEA grid title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <div className="mt-5 grid grid-cols-3 gap-2" data-testid="fmea-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)} className={`rounded-sm border px-3 py-2.5 text-center transition-all ${step === s.n ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold block ${step === s.n ? "text-blue-700" : "text-slate-400"}`}>{s.label[0]}</span>
              <p className={`text-xs font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2><p className="text-sm text-slate-500">{cur.help}</p></div>
            {step === 1 && <button data-testid="fmea-add-item" onClick={addItem} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add failure mode</button>}
          </div>
          {items.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add failure modes in Step 1.</p>}
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it.id} data-testid={`fmea-item-${i}`} className={`flex flex-col md:flex-row md:items-center gap-2 border rounded-sm p-3 transition-colors ${isCritical(it) ? "border-slate-200 border-l-4 border-l-red-600 bg-red-50" : "border-slate-200"}`}>
                {step === 1 ? (
                  <>
                    <input data-testid={`fmea-step-${i}`} className={inputCls + " md:w-44"} value={it.step} onChange={(e) => setItem(i, { step: e.target.value })} placeholder="Process step" />
                    <input data-testid={`fmea-mode-${i}`} className={inputCls + " flex-1"} value={it.mode} onChange={(e) => setItem(i, { mode: e.target.value })} placeholder="Failure mode" />
                  </>
                ) : (
                  <span className="flex-1 text-sm text-slate-800">{it.step ? `${it.step} — ` : ""}{it.mode || <span className="text-slate-300">Unnamed mode</span>}</span>
                )}
                <div className="flex items-center gap-2">
                  {isCritical(it) && <span data-testid={`fmea-critical-chip-${i}`} className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-red-600 text-white whitespace-nowrap">Critical exposure</span>}
                  <span className="text-xs font-mono text-slate-400">{cur.label[0]}</span>
                  <select data-testid={`fmea-${cur.field}-${i}`} className={selCls} value={it[cur.field]} onChange={(e) => setItem(i, { [cur.field]: Number(e.target.value) })}>
                    {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {step === 3 && <span className="text-xs font-mono font-bold text-slate-700" data-testid={`fmea-live-rpn-${i}`}>RPN {rpn(it)}</span>}
                  {step === 1 && <button data-testid={`fmea-remove-${i}`} onClick={() => removeItem(i)} className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="fmea-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="fmea-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="fmea-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">Next <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="fmea-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Grid</button>
            )}
          </div>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="fmea" step={cur} phase={cur.phase} onApplySuggestion={(t) => { navigator.clipboard?.writeText(t); toast.info("Copied to clipboard"); }} />
    </Layout>
  );
}
